// ─────────────────────────────────────────────────────────────────────────────
// API client — fetches one checkout from the BFF.
//
// Production base: https://api.posteering.com
// Dev base:        same (CORS already permits http://localhost:3000)
//
// This module has ONE job: take a token, return either a typed
// PublicCheckoutResponse or a typed CheckoutError. It does not retry, does
// not cache, does not schedule. All loop logic lives in usePolling.ts.
//
// Timeout is 15s — deliberately LONGER than the BFF's own 10s upstream
// timeout to bank-rail. This lets the BFF's structured 504 BANKRAIL_TIMEOUT
// reach the browser rather than the browser aborting first with a generic
// network error (which would lose the structured error code and mask the
// real cause). See services/api-gateway/src/checkout/checkout.service.ts
// BANKRAIL_TIMEOUT_MS.
//
// No credentials, no auth headers. The token in the URL IS the capability.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  PublicCheckoutResponse,
  CheckoutError,
  CheckoutErrorCode,
} from './types';

// Allow override via Vite env for staging/local-backend testing.
// import.meta.env.VITE_API_BASE is baked in at build time by Vite.
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  'https://api.posteering.com';

const FETCH_TIMEOUT_MS = 15_000;

// Token regex mirrors services/api-gateway/src/checkout/checkout.controller.ts
// route constraint: exactly 43 chars of base64url alphabet.
// Used by App.tsx before any API call — malformed tokens render <InvalidLink />.
export const TOKEN_REGEX = /^[A-Za-z0-9_-]{43}$/;

// ─────────────────────────────────────────────────────────────────────────────
// Result type — discriminated union, forces callers to handle both branches.
// ─────────────────────────────────────────────────────────────────────────────

export type FetchResult =
  | { ok: true; data: PublicCheckoutResponse }
  | { ok: false; error: CheckoutError };

// ─────────────────────────────────────────────────────────────────────────────
// The fetch.
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchCheckout(
  token: string,
  signal?: AbortSignal
): Promise<FetchResult> {
  const url = `${API_BASE}/api/v1/checkout/${encodeURIComponent(token)}`;

  // Compose our own timeout AbortController with any caller-supplied signal,
  // so unmounting the polling hook cancels in-flight requests cleanly.
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutController.abort(),
    FETCH_TIMEOUT_MS
  );

  // Merge signals: abort if either the timeout OR the external signal fires.
  const composed = signal
    ? mergeSignals(signal, timeoutController.signal)
    : timeoutController.signal;

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: composed,
      // credentials intentionally omitted — no cookies, no auth, no identity
      // leakage. The token IS the capability.
      credentials: 'omit',
      cache: 'no-store',
    });

    if (res.ok) {
      const data = (await res.json()) as PublicCheckoutResponse;
      return { ok: true, data };
    }

    // Non-2xx — try to parse the structured error body.
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // Body wasn't JSON — fall through to generic error based on status.
    }

    return { ok: false, error: classifyErrorResponse(res.status, body) };
  } catch {
    // Network error, timeout, or abort. Distinguish abort-by-caller vs abort-
    // by-timeout: only the latter is reported; caller-aborts are silent.
    if (signal?.aborted) {
      return {
        ok: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'Request cancelled',
          httpStatus: null,
        },
      };
    }
    if (timeoutController.signal.aborted) {
      return {
        ok: false,
        error: {
          code: 'BANKRAIL_TIMEOUT',
          message:
            'The server took too long to respond. Retrying…',
          httpStatus: null,
        },
      };
    }
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message:
          'Could not reach the server. Check your connection and retry.',
        httpStatus: null,
        // Kept local for debugging; message is what the UI shows.
      },
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Map an HTTP status + (possibly-structured) body into a typed CheckoutError.
// Handles both the BFF's structured bodies and NestJS routing-layer bodies.
// ─────────────────────────────────────────────────────────────────────────────

function classifyErrorResponse(
  status: number,
  body: unknown
): CheckoutError {
  // Safely extract the `error` field if the body is our structured shape.
  const structured =
    body && typeof body === 'object'
      ? (body as { error?: unknown; message?: unknown })
      : null;

  const errorField =
    structured && typeof structured.error === 'string'
      ? (structured.error as string)
      : null;

  const messageField =
    structured && typeof structured.message === 'string'
      ? (structured.message as string)
      : null;

  // Prefer the structured `error` code when present. Otherwise, fall back
  // by HTTP status — covers the NestJS routing-layer 404 that lacks `error`.
  const code: CheckoutErrorCode =
    errorField === 'CHECKOUT_NOT_FOUND' ||
    errorField === 'TOO_MANY_REQUESTS' ||
    errorField === 'BANKRAIL_UNAVAILABLE' ||
    errorField === 'BANKRAIL_TIMEOUT'
      ? errorField
      : fallbackCodeForStatus(status);

  return {
    code,
    message: messageField ?? defaultMessageForCode(code),
    httpStatus: status,
  };
}

function fallbackCodeForStatus(status: number): CheckoutErrorCode {
  if (status === 404) return 'CHECKOUT_NOT_FOUND';
  if (status === 429) return 'TOO_MANY_REQUESTS';
  if (status === 504) return 'BANKRAIL_TIMEOUT';
  if (status >= 500) return 'BANKRAIL_UNAVAILABLE';
  return 'UNKNOWN_ERROR';
}

function defaultMessageForCode(code: CheckoutErrorCode): string {
  switch (code) {
    case 'CHECKOUT_NOT_FOUND':
      return 'This checkout link is not valid or has expired.';
    case 'TOO_MANY_REQUESTS':
      return 'Too many requests. Please wait a moment and retry.';
    case 'BANKRAIL_UNAVAILABLE':
      return 'Checkout data is temporarily unavailable. Please try again.';
    case 'BANKRAIL_TIMEOUT':
      return 'The server took too long to respond. Retrying…';
    case 'NETWORK_ERROR':
      return 'Could not reach the server. Check your connection and retry.';
    case 'UNKNOWN_ERROR':
      return 'Something went wrong. Please try again.';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Merge two AbortSignals into one. Native AbortSignal.any exists but only in
// very recent browsers (Chrome 116+, Safari 17.4+) — we polyfill for breadth.
// ─────────────────────────────────────────────────────────────────────────────

function mergeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (a.aborted) return a;
  if (b.aborted) return b;

  const controller = new AbortController();
  const onAbortA = (): void => controller.abort();
  const onAbortB = (): void => controller.abort();

  a.addEventListener('abort', onAbortA, { once: true });
  b.addEventListener('abort', onAbortB, { once: true });

  // Cleanup once either fires — prevents leaking listeners if one signal
  // sticks around long after the fetch resolves.
  controller.signal.addEventListener(
    'abort',
    () => {
      a.removeEventListener('abort', onAbortA);
      b.removeEventListener('abort', onAbortB);
    },
    { once: true }
  );

  return controller.signal;
}
